/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useState, useEffect } from 'react';
import FormBox from '../../common/FormBox/FormBox';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FaTrash } from 'react-icons/fa';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import AddEntryFooter from '../AddEntryFooter';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { isSalesPersonRole } from 'data/constants/roles';
import { useTranslation } from 'react-i18next';
import { formatDateToYMD, isFullDateFormat, parseStoredDate } from 'utils/fullDateFormat';
import ToggleButtonGroup from 'components/common/ToggleButtonGroup/ToggleButtonGroup';
import { fetchOptions } from './utils';
import useEnabledCountries from 'hooks/useEnabledCountries';

export interface ChildData {
    relation: string | null;
    firstname: string | null;
    middlename: string | null;
    lastname: string | null;
    birth_date: string | null;
    nationality: string | null;
    country_of_residence: string | null;
    accompanying: boolean | undefined;
}

const initChildEntry: ChildData = {
    relation: '',
    firstname: '',
    middlename: '',
    lastname: '',
    birth_date: '',
    nationality: '',
    country_of_residence: '',
    accompanying: undefined,
};

export interface FamilyData {
    relation: string | null;
    firstname: string | null;
    lastname: string | null;
    birth_date: string | null;
    // gender: string;
    nationality: string | null;
    country_of_residence: string | null;
    country_of_birth: string | null;
}

const initEntry: FamilyData = {
    relation: '',
    firstname: '',
    lastname: '',
    birth_date: '',
    nationality: '',
    country_of_residence: '',
    country_of_birth: '',
};

interface Msg {
    relation: string;
    firstname: string;
    lastname: string;
    birth_date: string;
    nationality: string;
    country_of_residence: string;
    country_of_birth: string;
}

export const initFamily: Msg = {
    relation: '',
    firstname: '',
    lastname: '',
    birth_date: '',
    nationality: '',
    country_of_residence: '',
    country_of_birth: '',
};

export interface ChildMsg {
    relation: string;
    firstname: string;
    middlename: string;
    lastname: string;
    birth_date: string;
    nationality: string;
    country_of_residence: string;
    accompanying: string;
}

const initChildMsg: ChildMsg = {
    relation: '',
    firstname: '',
    middlename: '',
    lastname: '',
    birth_date: '',
    nationality: '',
    country_of_residence: '',
    accompanying: '',
};

export default function FamilyMembers() {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { hasSelfServeMarket } = useEnabledCountries();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const [familyData, setFamilyData] = useState<FamilyData[]>([]);
    const [validationMsgFamily, setValidationMsgFamily] = useState<Msg[]>([initFamily]);
    const [hasFamily, setHasFamily] = useState<boolean>(false);
    const [hasChild, setHasChild] = useState<boolean>(false);
    const [childData, setChildData] = useState<ChildData[]>([]);
    const [validationMsgChild, setValidationMsgChild] = useState<ChildMsg[]>([initChildMsg]);

    const [isFamInfo, setIsFamInfo] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [childRelation, setChildRelation] = useState([]);
    const [nonChildRelation, setNonChildRelation] = useState([]);

    useEffect(() => {
        if (isFamInfo) {
            setIsLoading(false);
        }
    }, [isFamInfo]);

    // FAMILY update
    const handleUpdateHasFamily = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasFamily(true);
            setFamilyData([initEntry]);
            setValidationMsgFamily([initFamily]);
        } else if (value === t('no', { lng: language })) {
            setHasFamily(false);
            setFamilyData([]);
            setValidationMsgFamily([]);
        }
    };

    const handleUpdateFamily = (idx: number, field: string, value: string | any) => {
        validate(field, value, undefined, idx, 'familymember');
        const valueWithoutSeparators: string =
            field !== 'accompanying' ? value?.replaceAll('.', '').split(',').join('') : value;

        const temp = [...familyData];
        temp[idx] = { ...familyData[idx], [field]: valueWithoutSeparators };
        setFamilyData(temp);
    };

    const handleDateUpdateFamily = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            validate(field, '', undefined, idx, 'familymember');
            const temp_Family = [...validationMsgFamily];
            temp_Family[idx] = {
                ...temp_Family[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsgFamily(temp_Family);
            const temp = [...familyData];
            temp[idx] = { ...familyData[idx], [field]: '' };
            setFamilyData(temp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp_Family = [...validationMsgFamily];
            temp_Family[idx] = { ...temp_Family[idx], [field]: 'Invalid date format' };
            setValidationMsgFamily(temp_Family);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep day/month visible
            const temp_Family = [...validationMsgFamily];
            temp_Family[idx] = { ...temp_Family[idx], [field]: 'Invalid date format' };
            setValidationMsgFamily(temp_Family);
            const temp = [...familyData];
            temp[idx] = { ...familyData[idx], [field]: extractDateFormat };
            setFamilyData(temp);
            return;
        }

        const temp_Family = [...validationMsgFamily];
        temp_Family[idx] = { ...temp_Family[idx], [field]: '' };
        setValidationMsgFamily(temp_Family);
        validate(field, extractDateFormat, undefined, idx, 'familymember');
        const temp = [...familyData];
        temp[idx] = { ...familyData[idx], [field]: extractDateFormat };
        setFamilyData(temp);
    };

    const handleAddFamily = () => {
        const temp: FamilyData[] = [...familyData, initEntry];
        setFamilyData(temp);
        const tempValidation: Msg[] = [...validationMsgFamily, initFamily];
        setValidationMsgFamily(tempValidation);
    };

    const handleDeleteFamily = (idx: number) => {
        const temp: FamilyData[] = [...familyData];
        temp.splice(idx, 1);
        setFamilyData(temp);

        const tempMsg: Msg[] = [...validationMsgFamily];
        tempMsg.splice(idx, 1);
        setValidationMsgFamily(tempMsg);
    };

    //Child update
    const handleUpdateHasChild = (value: string) => {
        if (value === t('yes', { lng: language })) {
            setHasChild(true);
            setChildData([initChildEntry]);
            setValidationMsgChild([initChildMsg]);
        } else if (value === t('no', { lng: language })) {
            setHasChild(false);
            setChildData([]);
            setValidationMsgChild([]);
        }
    };

    const handleUpdateChild = (idx: number, field: string, value: string | any) => {
        validate(field, value, undefined, idx, 'child');
        const valueWithoutSeparators: string =
            field !== 'accompanying' ? value?.replaceAll('.', '').split(',').join('') : value;

        const temp = [...childData];
        temp[idx] = { ...childData[idx], [field]: valueWithoutSeparators };
        setChildData(temp);
    };

    const handleDateUpdateChild = (idx: number, field: string, newDate: Date | null) => {
        if (newDate === null) {
            validate(field, '', undefined, idx, 'child');
            const temp_Child = [...validationMsgChild];
            temp_Child[idx] = {
                ...temp_Child[idx],
                [field]: t('requiredField', { lng: language }),
            };
            setValidationMsgChild(temp_Child);
            const temp = [...childData];
            temp[idx] = { ...childData[idx], [field]: '' };
            setChildData(temp);
            return;
        }
        if (!(newDate instanceof Date) || isNaN(newDate.getTime())) {
            const temp_Child = [...validationMsgChild];
            temp_Child[idx] = { ...temp_Child[idx], [field]: 'Invalid date format' };
            setValidationMsgChild(temp_Child);
            return;
        }

        const extractDateFormat = formatDateToYMD(newDate);

        if (!isFullDateFormat(newDate)) {
            // Incomplete year while typing — keep day/month visible
            const temp_Child = [...validationMsgChild];
            temp_Child[idx] = { ...temp_Child[idx], [field]: 'Invalid date format' };
            setValidationMsgChild(temp_Child);
            const temp = [...childData];
            temp[idx] = { ...childData[idx], [field]: extractDateFormat };
            setChildData(temp);
            return;
        }

        const temp_Child = [...validationMsgChild];
        temp_Child[idx] = { ...temp_Child[idx], [field]: '' };
        setValidationMsgChild(temp_Child);
        validate(field, extractDateFormat, undefined, idx, 'child');
        const temp = [...childData];
        temp[idx] = { ...childData[idx], [field]: extractDateFormat };
        setChildData(temp);
    };

    const handleAddChild = () => {
        const temp: ChildData[] = [...childData, initChildEntry];
        setChildData(temp);
        const tempValidation: ChildMsg[] = [...validationMsgChild, initChildMsg];
        setValidationMsgChild(tempValidation);
    };

    const handleDeleteChild = (idx: number) => {
        const temp: ChildData[] = [...childData];
        temp.splice(idx, 1);
        setChildData(temp);

        const tempMsg: ChildMsg[] = [...validationMsgChild];
        tempMsg.splice(idx, 1);
        setValidationMsgChild(tempMsg);
    };

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);

    const isSalesPerson = isSalesPersonRole(role);

    const loadFamData = async (val: any) => {
        if (val && val.length | Object.keys(val).length) {
            setFamilyData(val);
            setHasFamily(true);
        }
        if (val == null || val) {
            setIsFamInfo(true);
        } else {
            setIsFamInfo(false);
        }
    };

    const loadChildData = async (val: any) => {
        if (val && val.length | Object.keys(val).length) {
            setChildData(val);
            setHasChild(true);
        }
        // if (val == null || val) {
        //     setIsFamInfo(true);
        // } else {
        //     setIsFamInfo(false);
        // }
    };

    const [nationalities, setNationalities] = useState<string[]>([]);
    const [countryOptions, setCountryOptions] = useState<string[]>([]);

    const getNationalityOptions = () =>
        fetchOptions(api?.assessment_options.getNationalityOptions, setNationalities);
    const getCountryOptions = () =>
        fetchOptions(api?.assessment_options.getCountryOptions, setCountryOptions);

    useEffect(() => {
        getNationalityOptions();
        getCountryOptions();
    }, []);

    const nationalityOptions = nationalities.length > 0 ? nationalities : countryOptions;

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsFamInfo(false);
            setFamilyData([]);
            if (isSalesPerson) {
                api.tableInput.fetchCandidateInputSales(loadFamData, 'FamilyMember', candidate_id);
                api.tableInput.fetchCandidateInputSales(loadChildData, 'Child', candidate_id);
            } else {
                api.tableInput.fetchCandidateInput(loadFamData, 'FamilyMember');
            }
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, candidate_id]);

    // FIELD VALIDATION
    const validate = async (
        field: string,
        value: string,
        valid?: boolean,
        idx?: number,
        section?: string
    ) => {
        let validated = true;

        //Family
        const temp_Family = [...validationMsgFamily];
        const temp_Child = [...validationMsgChild];
        if (!field && !value) {
            for (let i = 0; i < familyData.length; i++) {
                for (const key of Object.keys(initFamily)) {
                    const value = familyData[i][key as keyof FamilyData];

                    let msg = '';
                    if (key === 'firstname' && section === 'familymember') {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        temp_Family[i] = { ...temp_Family[i], [key]: msg };
                        setValidationMsgFamily(temp_Family);
                    } else if (section === undefined) {
                        if (value === '' || value === null || value === undefined) {
                            if (key === 'country_of_birth') continue;
                            msg = t('requiredField', { lng: language });
                            validated = false;
                            temp_Family[i] = { ...temp_Family[i], [key]: msg };
                            setValidationMsgFamily(temp_Family);
                        }
                    }
                }
            }

            for (let i = 0; i < childData.length; i++) {
                for (const key of Object.keys(initChildMsg)) {
                    const value = childData[i][key as keyof ChildData];
                    let msg = '';
                    if (childData[i].accompanying === false && key === 'firstname') continue;
                    if (childData[i].accompanying === false && key === 'lastname') continue;
                    // if (childData[i].accompanying === false && key === 'nationality') continue;
                    if (key === 'middlename') continue;
                    else if (value === '' || value === null || value === undefined) {
                        msg = t('requiredField', { lng: language });
                        validated = false;
                        temp_Child[i] = { ...temp_Child[i], [key]: msg };
                        setValidationMsgChild(temp_Child);
                    }
                }
            }
        } else {
            if (section === 'child') {
                let msg = '';
                if (field === 'firstname' && (value === '' || value === null)) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                    temp_Child[idx!] = { ...temp_Child[idx!], [field]: msg };
                    setValidationMsgChild(temp_Child);
                } else if (value === '' || value === null) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp_Child[idx!] = { ...temp_Child[idx!], [field]: msg };
                setValidationMsgChild(temp_Child);
            } else {
                let msg = '';
                if (
                    field === 'firstname' &&
                    section === 'familymember' &&
                    (value === '' || value === null)
                ) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                    temp_Family[idx!] = { ...temp_Family[idx!], [field]: msg };
                    setValidationMsgFamily(temp_Family);
                } else if (value === '' || value === null) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp_Family[idx!] = { ...temp_Family[idx!], [field]: msg };
                setValidationMsgFamily(temp_Family);
            }
        }
        return validated;
    };

    useEffect(() => {
        getChildRelation();
        getNonChildRelation();
    }, []);

    const getChildRelation = async () => {
        try {
            const resp = await api.assessment_options.getRelationChild();
            if (resp?.status === 200 || resp?.status === 201) {
                setChildRelation(resp?.data);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getNonChildRelation = async () => {
        try {
            const resp = await api.assessment_options.getRelationNonChild();
            if (resp?.status === 200 || resp?.status === 201) {
                setNonChildRelation(resp?.data);
            }
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <>
                    <FormBox
                        validation={validate}
                        tableNames={['FamilyMember', 'Child']}
                        data={[familyData, childData]}
                    >
                        <>
                            <ToggleButtonGroup
                                customStyle={true}
                                options={[t('yes', { lng: language }), t('no', { lng: language })]}
                                name="have_child"
                                label={t('doYouHaveChildren', {
                                    lng: language,
                                })}
                                value={
                                    hasChild
                                        ? t('yes', { lng: language })
                                        : t('no', { lng: language })
                                }
                                onChange={(_: string, value: string) => handleUpdateHasChild(value)}
                            />
                            {hasChild &&
                                childData.map((f, i) => {
                                    return (
                                        <S.EntryCard key={i}>
                                            <S.SubTitle>
                                                <>{`${t('child', { lng: language })} ${i + 1}`}</>
                                                <S.DeleteButton style={{ marginLeft: 'auto' }}>
                                                    <button
                                                        onClick={() => {
                                                            handleDeleteChild(i);
                                                        }}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </S.DeleteButton>
                                            </S.SubTitle>
                                            <S.CardContent>
                                                <S.GridSection>
                                                    <Autocomplete
                                                        value={childData[i].relation}
                                                        id="relation"
                                                        options={childRelation}
                                                        onChange={(_, value) =>
                                                            handleUpdateChild(i, 'relation', value)
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'relation',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'child'
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('relation', { lng: language })}`}
                                                                error={Boolean(
                                                                    validationMsgChild[i]?.relation
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]?.relation
                                                                }
                                                            />
                                                        )}
                                                    />

                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                    >
                                                        <DesktopDatePicker
                                                            label={`*${t('dateOfBirth', { lng: language })}`}
                                                            format="dd/MM/yyyy"
                                                            value={parseStoredDate(
                                                                childData[i].birth_date
                                                            )}
                                                            onChange={(date: Date | null) => {
                                                                handleDateUpdateChild(
                                                                    i,
                                                                    'birth_date',
                                                                    date
                                                                );
                                                            }}
                                                            slotProps={{
                                                                field: { clearable: true },
                                                                textField: {
                                                                    error: Boolean(
                                                                        validationMsgChild[i]
                                                                            ?.birth_date
                                                                    ),
                                                                    helperText:
                                                                        validationMsgChild[i]
                                                                            ?.birth_date,
                                                                    title: t(
                                                                        'payAttentionToFormat',
                                                                        { lng: language }
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                    <Autocomplete
                                                        value={childData[i].nationality}
                                                        id="nationality"
                                                        options={countryOptions}
                                                        onChange={(_, value) =>
                                                            handleUpdateChild(
                                                                i,
                                                                'nationality',
                                                                value
                                                            )
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'nationality',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'child'
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('nationality', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsgChild[i]
                                                                        ?.nationality
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]
                                                                        ?.nationality
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <Autocomplete
                                                        disablePortal
                                                        value={childData[i].country_of_residence}
                                                        id="residence"
                                                        options={countryOptions}
                                                        onChange={(_, value) =>
                                                            handleUpdateChild(
                                                                i,
                                                                'country_of_residence',
                                                                value
                                                            )
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'country_of_residence',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'child'
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('countryOfResidence', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsgChild[i]
                                                                        ?.country_of_residence
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]
                                                                        ?.country_of_residence
                                                                }
                                                            />
                                                        )}
                                                    />

                                                    <ToggleButtonGroup
                                                        options={[
                                                            t('yes', { lng: language }),
                                                            t('no', { lng: language }),
                                                        ]}
                                                        name="accompanying"
                                                        label={t('travellingWithYou', {
                                                            lng: language,
                                                        })}
                                                        value={
                                                            childData[i].accompanying ===
                                                                undefined ||
                                                            childData[i].accompanying === null
                                                                ? undefined
                                                                : childData[i].accompanying
                                                                  ? t('yes', { lng: language })
                                                                  : t('no', { lng: language })
                                                        }
                                                        onChange={(arg1: string, arg2: string) =>
                                                            handleUpdateChild(
                                                                i,
                                                                arg1,
                                                                arg2 === t('yes', { lng: language })
                                                            )
                                                        }
                                                        error={validationMsgChild[i]?.accompanying}
                                                    />
                                                    {/* <ToggleButtonGroup
                                    options={['Male', 'Female', 'Other']}
                                    name="gender"
                                    label={'Gender'}
                                    value={familyData[i].gender}
                                    onChange={(arg1: string, arg2: string) =>
                                        handleUpdateFamily(i, arg1, arg2)
                                    }
                                    error={validationMsgFamily[i].gender}
                                /> */}
                                                    {Boolean(childData[i].accompanying) && (
                                                        <>
                                                            <TextField
                                                                id="firstName"
                                                                label={`*${t('firstName', { lng: language })}`}
                                                                type="text"
                                                                value={childData[i].firstname}
                                                                onChange={(e) => {
                                                                    handleUpdateChild(
                                                                        i,
                                                                        'firstname',
                                                                        e.target.value
                                                                    );
                                                                }}
                                                                onFocus={(
                                                                    e: React.FocusEvent<HTMLInputElement>
                                                                ) => {
                                                                    validate(
                                                                        'firstname',
                                                                        e.target.value,
                                                                        undefined,
                                                                        i,
                                                                        'child'
                                                                    );
                                                                }}
                                                                error={Boolean(
                                                                    validationMsgChild[i]?.firstname
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]?.firstname
                                                                }
                                                            />
                                                            <TextField
                                                                id="middleName"
                                                                label={`${t('middlename', { lng: language })}`}
                                                                type="text"
                                                                value={childData[i].middlename}
                                                                onChange={(e) => {
                                                                    handleUpdateChild(
                                                                        i,
                                                                        'middlename',
                                                                        e.target.value
                                                                    );
                                                                }}
                                                                onFocus={(
                                                                    e: React.FocusEvent<HTMLInputElement>
                                                                ) => {
                                                                    validate(
                                                                        'middlename',
                                                                        e.target.value,
                                                                        undefined,
                                                                        i,
                                                                        'child'
                                                                    );
                                                                }}
                                                                error={Boolean(
                                                                    validationMsgChild[i]
                                                                        ?.middlename
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]
                                                                        ?.middlename
                                                                }
                                                            />
                                                            <TextField
                                                                id="lastName"
                                                                label={`*${t('lastName', { lng: language })}`}
                                                                type="text"
                                                                value={childData[i].lastname}
                                                                onChange={(e) => {
                                                                    handleUpdateChild(
                                                                        i,
                                                                        'lastname',
                                                                        e.target.value
                                                                    );
                                                                }}
                                                                onFocus={(
                                                                    e: React.FocusEvent<HTMLInputElement>
                                                                ) => {
                                                                    validate(
                                                                        'lastname',
                                                                        e.target.value,
                                                                        undefined,
                                                                        i,
                                                                        'child'
                                                                    );
                                                                }}
                                                                error={Boolean(
                                                                    validationMsgChild[i]?.lastname
                                                                )}
                                                                helperText={
                                                                    validationMsgChild[i]?.lastname
                                                                }
                                                            />
                                                        </>
                                                    )}
                                                </S.GridSection>
                                            </S.CardContent>
                                        </S.EntryCard>
                                    );
                                })}

                            {hasChild && (
                                <AddEntryFooter
                                    title={t('child', { lng: language })}
                                    addLabel={t('addChild', { lng: language })}
                                    addAnotherLabel={t('addAnotherChild', { lng: language })}
                                    hasEntries={childData.length > 0}
                                    onClick={() => handleAddChild()}
                                />
                            )}
                        </>
                        <section style={{ marginTop: '50px' }}>
                            <ToggleButtonGroup
                                customStyle={true}
                                options={[t('yes', { lng: language }), t('no', { lng: language })]}
                                name="have_family"
                                label={t('travelWithFamily', {
                                    lng: language,
                                })}
                                value={
                                    hasFamily
                                        ? t('yes', { lng: language })
                                        : t('no', { lng: language })
                                }
                                onChange={(_: string, value: string) =>
                                    handleUpdateHasFamily(value)
                                }
                            />
                            {hasFamily &&
                                familyData.map((f, i) => {
                                    return (
                                        <S.EntryCard key={i}>
                                            <S.SubTitle>
                                                <>{`${t('familyMember', { lng: language })} ${i + 1}`}</>
                                                <S.DeleteButton style={{ marginLeft: 'auto' }}>
                                                    <button
                                                        onClick={() => {
                                                            handleDeleteFamily(i);
                                                        }}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </S.DeleteButton>
                                            </S.SubTitle>
                                            <S.CardContent>
                                                <S.GridSection>
                                                    <Autocomplete
                                                        value={familyData[i].relation}
                                                        id="relation"
                                                        options={nonChildRelation}
                                                        onChange={(_, value) =>
                                                            handleUpdateFamily(i, 'relation', value)
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'relation',
                                                                e.target.value,
                                                                undefined,
                                                                i
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('relation', { lng: language })}`}
                                                                error={Boolean(
                                                                    validationMsgFamily[i]?.relation
                                                                )}
                                                                helperText={
                                                                    validationMsgFamily[i]?.relation
                                                                }
                                                            />
                                                        )}
                                                    />

                                                    <TextField
                                                        id="firstName"
                                                        label={`*${t('firstName', { lng: language })}`}
                                                        type="text"
                                                        value={familyData[i].firstname}
                                                        onChange={(e) => {
                                                            handleUpdateFamily(
                                                                i,
                                                                'firstname',
                                                                e.target.value
                                                            );
                                                        }}
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'firstname',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'familymember'
                                                            );
                                                        }}
                                                        error={Boolean(
                                                            validationMsgFamily[i]?.firstname
                                                        )}
                                                        helperText={
                                                            validationMsgFamily[i]?.firstname
                                                        }
                                                    />

                                                    <TextField
                                                        id="lastName"
                                                        label={`*${t('lastName', { lng: language })}`}
                                                        type="text"
                                                        value={familyData[i].lastname}
                                                        onChange={(e) => {
                                                            handleUpdateFamily(
                                                                i,
                                                                'lastname',
                                                                e.target.value
                                                            );
                                                        }}
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'lastname',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'familymember'
                                                            );
                                                        }}
                                                        error={Boolean(
                                                            validationMsgFamily[i]?.lastname
                                                        )}
                                                        helperText={
                                                            validationMsgFamily[i]?.lastname
                                                        }
                                                    />
                                                    <LocalizationProvider
                                                        dateAdapter={AdapterDateFns}
                                                    >
                                                        <DesktopDatePicker
                                                            label={`*${t('dateOfBirth', { lng: language })}`}
                                                            format="dd/MM/yyyy"
                                                            value={parseStoredDate(
                                                                familyData[i].birth_date
                                                            )}
                                                            onChange={(date: Date | null) => {
                                                                handleDateUpdateFamily(
                                                                    i,
                                                                    'birth_date',
                                                                    date
                                                                );
                                                            }}
                                                            slotProps={{
                                                                field: { clearable: true },
                                                                textField: {
                                                                    error: Boolean(
                                                                        validationMsgFamily[i]
                                                                            ?.birth_date
                                                                    ),
                                                                    helperText:
                                                                        validationMsgFamily[i]
                                                                            ?.birth_date,
                                                                    title: t(
                                                                        'payAttentionToFormat',
                                                                        { lng: language }
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                    {/* <ToggleButtonGroup
                                    options={['Male', 'Female', 'Other']}
                                    name="gender"
                                    label={'Gender'}
                                    value={familyData[i].gender}
                                    onChange={(arg1: string, arg2: string) =>
                                        handleUpdateFamily(i, arg1, arg2)
                                    }
                                    error={validationMsgFamily[i].gender}
                                /> */}
                                                    <Autocomplete
                                                        value={familyData[i].nationality}
                                                        id="nationality"
                                                        options={nationalityOptions}
                                                        onChange={(_, value) =>
                                                            handleUpdateFamily(
                                                                i,
                                                                'nationality',
                                                                value
                                                            )
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'nationality',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'familymember'
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('nationality', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsgFamily[i]
                                                                        ?.nationality
                                                                )}
                                                                helperText={
                                                                    validationMsgFamily[i]
                                                                        ?.nationality
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <Autocomplete
                                                        disablePortal
                                                        value={familyData[i].country_of_residence}
                                                        id="residence"
                                                        options={countryOptions}
                                                        onChange={(_, value) =>
                                                            handleUpdateFamily(
                                                                i,
                                                                'country_of_residence',
                                                                value
                                                            )
                                                        }
                                                        onFocus={(
                                                            e: React.FocusEvent<HTMLInputElement>
                                                        ) => {
                                                            validate(
                                                                'country_of_residence',
                                                                e.target.value,
                                                                undefined,
                                                                i,
                                                                'familymember'
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={`*${t('countryOfResidence', {
                                                                    lng: language,
                                                                })}`}
                                                                error={Boolean(
                                                                    validationMsgFamily[i]
                                                                        ?.country_of_residence
                                                                )}
                                                                helperText={
                                                                    validationMsgFamily[i]
                                                                        ?.country_of_residence
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    {hasSelfServeMarket && (
                                                        <Autocomplete
                                                            disablePortal
                                                            value={familyData[i].country_of_birth}
                                                            id="country_of_birth"
                                                            options={countryOptions}
                                                            onChange={(_, value) =>
                                                                handleUpdateFamily(
                                                                    i,
                                                                    'country_of_birth',
                                                                    value
                                                                )
                                                            }
                                                            onFocus={(
                                                                e: React.FocusEvent<HTMLInputElement>
                                                            ) => {
                                                                validate(
                                                                    'country_of_birth',
                                                                    e.target.value,
                                                                    undefined,
                                                                    i,
                                                                    'familymember'
                                                                );
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={`${t('countryOfBirth', {
                                                                        lng: language,
                                                                    })}`}
                                                                    // error={Boolean(
                                                                    //     validationMsgFamily[i]
                                                                    //         ?.country_of_birth
                                                                    // )}
                                                                    // helperText={
                                                                    //     validationMsgFamily[i]
                                                                    //         ?.country_of_birth
                                                                    // }
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </S.GridSection>
                                            </S.CardContent>
                                        </S.EntryCard>
                                    );
                                })}

                            {hasFamily && (
                                <AddEntryFooter
                                    title={t('familyMember', { lng: language })}
                                    addLabel={t('addFamilyMember', { lng: language })}
                                    addAnotherLabel={t('addAnotherFamilyMember', {
                                        lng: language,
                                    })}
                                    hasEntries={familyData.length > 0}
                                    onClick={() => handleAddFamily()}
                                />
                            )}
                        </section>
                    </FormBox>
                </>
            )}
        </>
    );
}
